import clsx from 'clsx'
import {Field, FieldProps} from 'formik'
import MaskedInput from 'react-text-mask'
import createNumberMask from 'text-mask-addons/dist/createNumberMask'

interface Props {
    name: string
    label?: string
    type?: string
    value?: string
    placeholder?: string
    onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void
    onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void
    required?: boolean
    allowNegative?: boolean
    readOnly?: boolean
    disabled?: boolean
    calculatorMode?: boolean
}

function formatMoneyCalc(rawValue: string): string {
    const digits = rawValue.replace(/\D/g, '')
    if (!digits) return ''
    const number = parseInt(digits, 10)
    if (!number) return ''
    const reais = Math.floor(number / 100)
    const cents = (number % 100).toString().padStart(2, '0')
    const reaisFormatted = reais.toLocaleString('pt-BR')
    return `R$${reaisFormatted},${cents}`
}
export function TextInputMoneyMask({
    name,
    label,
    type = 'text',
    value,
    placeholder,
    onChange,
    onBlur,
    required = false,
    allowNegative = true,
    readOnly = false,
    disabled = false,
    calculatorMode = false,
    ...props
}: Props) {
    const defaultMaskOptions = {
        prefix: 'R$',
        suffix: '',
        includeThousandsSeparator: true,
        thousandsSeparatorSymbol: '',
        allowDecimal: true,
        decimalSymbol: '.',
        decimalLimit: 2, // how many digits allowed after the decimal
        integerLimit: 8, // limit length of integer numbers
        allowNegative: allowNegative,
        allowLeadingZeroes: false,
    }

    const currencyMask = createNumberMask({
        ...defaultMaskOptions,
    })

    return (
        <>
            {label && (
                <label htmlFor={name} className='form-label fs-6 fw-bolder'>
                    {label}
                    {required && <span className='text-danger'>*</span>}
                </label>
            )}
            <Field name={name}>
                {({field, form}: FieldProps) => {
                    const sharedProps = {
                        id: name,
                        placeholder,
                        disabled,
                        readOnly,
                        onBlur: onBlur || field.onBlur,
                        className: clsx(
                            'form-control form-control-lg form-control-solid',
                            {'is-invalid': form?.touched[name] && form?.errors[name]},
                            {
                                'is-valid':
                                    !calculatorMode && form?.touched[name] && !form?.errors[name],
                            }
                        ),
                    }
                    return (
                        <>
                            {calculatorMode ? (
                                <input
                                    {...sharedProps}
                                    type='text'
                                    inputMode='numeric'
                                    value={value !== undefined ? value : field.value}
                                    onChange={(e) => {
                                        form.setFieldValue(name, formatMoneyCalc(e.target.value))
                                        if (onChange) onChange(e)
                                    }}
                                />
                            ) : (
                                <MaskedInput
                                    {...props}
                                    {...sharedProps}
                                    mask={currencyMask}
                                    type={type}
                                    value={value || form.values[name]}
                                    onChange={onChange || form.handleChange}
                                />
                            )}
                            {form.touched[name] && form.errors[name] && (
                                <div className='fv-plugins-message-container'>
                                    <div className='fv-help-block'>
                                        <span role='alert' className='text-danger'>
                                            {form.errors[name] as string}
                                        </span>
                                    </div>
                                </div>
                            )}
                        </>
                    )
                }}
            </Field>
        </>
    )
}
